/**
 * Application Configuration
 *
 * This file contains all configuration constants for the application.
 * Backend functionality is handled through the Security Service and MageAI APIs.
 *
 * For secrets and API keys, use environment variables.
 * See ARCHITECTURE.md for more information.
 */

export const APP_CONFIG = {
  // Application metadata
  app: {
    publicUrl:
      process.env.NEXT_PUBLIC_BASE_URL ?? "https://www.nuclearworkers.ai",
    name: "NPXai Workforce",
    version: "1.0.0",
    description: "Nuclear Power Plant Workforce Management System",
  },

  // UI Configuration
  ui: {
    itemsPerPage: 10,
    maxFileSize: 10 * 1024 * 1024, // 10MB
    supportedImageTypes: ["image/jpeg", "image/png", "image/gif", "image/webp"],
    supportedDocumentTypes: [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ],
  },

  // Workflow Configuration
  workflows: {
    defaultTimeout: 30000, // 30 seconds
    maxRetries: 3,
    qualityAssurance: {
      categories: ["NCR", "OFI", "LL"] as const,
      stages: {
        intake: "Intake",
        screening: "Screening",
        correctiveActions: "Corrective Actions",
        implement: "Implement",
        effectivenessReview: "Effectiveness Review",
      },
    },
  },

  // Alert Configuration
  alerts: {
    priorities: ["low", "medium", "high", "critical"] as const,
    statuses: ["pending", "in_progress", "completed", "resolved"] as const,
    autoRefreshInterval: 30000, // 30 seconds
  },

  // Security Configuration
  security: {
    maxLoginAttempts: 5,
    lockoutDuration: 15 * 60 * 1000, // 15 minutes
    sessionTimeout: 24 * 60 * 60 * 1000, // 24 hours
  },

  // API Configuration
  api: {
    endpoints: {
      triage: "/webhook/triage",
      workflowIntake: "/wm/intake",
      workflowIntakeDirect: "/wm/intake_direct",
      workflowScreen: "/wm/screening",
      workflowPlan: "/wm/planning",
      workflowExecution: "/wm/execution",
      performanceImprovement: {
        intake: "/pi/intake",
        intakeRerun: "/pi/intake_rerun",
        screen: "/pi/screening",
        actions: "/pi/corrective-actions",
        implement: "/pi/implement",
        review: "/pi/effectiveness-review",
      },
    },
  },
} as const;

// Type exports for better TypeScript support
export type QualityAssuranceCategory =
  (typeof APP_CONFIG.workflows.qualityAssurance.categories)[number];
export type AlertPriority = (typeof APP_CONFIG.alerts.priorities)[number];
export type AlertStatus = (typeof APP_CONFIG.alerts.statuses)[number];
